import { useState } from "react";
import { monthlyRevenue } from "@/mocks/admin/payments";

export default function PaymentsRevenueChart() {
  const [hovered, setHovered] = useState<number | null>(null);

  const max = Math.max(...monthlyRevenue.map((m) => m.revenue));
  const niceMax = Math.ceil(max / 25000) * 25000; // round up to nearest 25k
  const avg =
    monthlyRevenue.reduce((s, m) => s + m.revenue, 0) / monthlyRevenue.length;
  const total = monthlyRevenue.reduce((s, m) => s + m.revenue, 0);
  const totalRefunds = monthlyRevenue.reduce((s, m) => s + m.refunds, 0);
  const avgPct = (avg / niceMax) * 100;

  // Y-axis gridlines: 0, 25%, 50%, 75%, 100% of niceMax
  const gridSteps = [1, 0.75, 0.5, 0.25, 0];

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-6">
      <div className="flex items-start justify-between mb-1 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-ink-900">Revenue Trend</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Monthly net revenue (after refunds)
          </p>
        </div>
        <div className="flex items-center gap-1 bg-cream-100 p-1 rounded-full">
          <button className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white text-ink-900 cursor-pointer whitespace-nowrap">
            6 months
          </button>
          <button className="text-xs font-semibold px-3 py-1.5 rounded-full text-ink-500 hover:text-ink-900 cursor-pointer whitespace-nowrap">
            12 months
          </button>
          <button className="text-xs font-semibold px-3 py-1.5 rounded-full text-ink-500 hover:text-ink-900 cursor-pointer whitespace-nowrap">
            All time
          </button>
        </div>
      </div>

      {/* Summary line */}
      <div className="flex items-baseline gap-3 mt-4 mb-6 flex-wrap">
        <span className="font-display text-3xl font-bold text-ink-900 tabular-nums">
          ${total.toLocaleString()}
        </span>
        <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
          <i className="ri-arrow-up-line"></i>
          22.4%
        </span>
        <span className="text-xs text-ink-400">vs. previous 6 months</span>
        <span className="text-ink-300">·</span>
        <span className="text-xs text-ink-500">
          Avg{" "}
          <span className="font-bold text-ink-900 tabular-nums">
            ${Math.round(avg / 1000)}k
          </span>{" "}
          / month
        </span>
        <span className="text-ink-300">·</span>
        <span className="text-xs text-ink-500">
          Refunds{" "}
          <span className="font-bold text-rose-700 tabular-nums">
            ${(totalRefunds / 1000).toFixed(1)}k
          </span>
        </span>
      </div>

      {/* Chart */}
      <div className="relative pl-12 pr-2">
        {/* Y-axis labels + gridlines */}
        <div className="relative h-56">
          {gridSteps.map((step, i) => {
            const val = niceMax * step;
            return (
              <div
                key={i}
                className="absolute left-0 right-0 flex items-center"
                style={{ top: `${(1 - step) * 100}%` }}
              >
                <span className="absolute -left-12 -translate-y-1/2 text-[10px] font-bold text-ink-400 tabular-nums whitespace-nowrap">
                  ${(val / 1000).toFixed(0)}k
                </span>
                <span className="w-full border-t border-dashed border-ink-100"></span>
              </div>
            );
          })}

          {/* Average line */}
          <div
            className="absolute left-0 right-0 flex items-center pointer-events-none"
            style={{ top: `${100 - avgPct}%` }}
          >
            <span className="w-full border-t-2 border-dashed border-brand-300/80"></span>
            <span className="absolute right-0 -top-2.5 text-[9px] font-bold text-brand-700 bg-white px-1.5 rounded">
              avg
            </span>
          </div>

          {/* Bars */}
          <div className="absolute inset-0 flex items-end justify-between gap-3 px-0">
            {monthlyRevenue.map((m, idx) => {
              const h = (m.revenue / niceMax) * 100;
              const refundH = (m.refunds / niceMax) * 100;
              const isPeak = m.revenue === max;
              const isHovered = hovered === idx;
              return (
                <div
                  key={m.month}
                  onMouseEnter={() => setHovered(idx)}
                  onMouseLeave={() => setHovered(null)}
                  className="relative flex-1 h-full flex flex-col justify-end items-center cursor-pointer"
                >
                  {/* Tooltip on hover */}
                  {isHovered && (
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-ink-900 text-white px-2.5 py-2 rounded-md shadow-lg z-10 whitespace-nowrap pointer-events-none">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-0.5">
                        {m.month} 2026
                      </div>
                      <div className="text-xs font-bold tabular-nums">
                        ${m.revenue.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-rose-300 tabular-nums">
                        -${m.refunds.toLocaleString()} refunds
                      </div>
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-ink-900 rotate-45"></div>
                    </div>
                  )}

                  <div
                    className="relative w-full max-w-[44px] rounded-t-md overflow-hidden transition-all flex flex-col-reverse"
                    style={{ height: `${h}%` }}
                  >
                    <div
                      className={`w-full transition-colors ${
                        isPeak
                          ? "bg-brand-500"
                          : isHovered
                          ? "bg-ink-700"
                          : "bg-ink-800"
                      }`}
                      style={{ height: `${100 - (refundH / h) * 100}%` }}
                    />
                    <div
                      className="w-full bg-rose-400"
                      style={{ height: `${(refundH / h) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="flex items-center justify-between gap-3 mt-2 pt-2">
          {monthlyRevenue.map((m) => (
            <div
              key={m.month}
              className="flex-1 text-center text-[11px] font-semibold text-ink-500"
            >
              {m.month}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t border-ink-100 text-[11px]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-ink-800"></span>
          <span className="text-ink-600 font-semibold">Net Revenue</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-rose-400"></span>
          <span className="text-ink-600 font-semibold">Refunds</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-brand-500"></span>
          <span className="text-ink-600 font-semibold">Peak month</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-0.5 border-t-2 border-dashed border-brand-300"></span>
          <span className="text-ink-600 font-semibold">6-mo avg</span>
        </span>
      </div>
    </div>
  );
}